import { authedApiFetch } from '../utils/Api';

/**
 * Service for handling input signals API calls
 */
const inputSignalsService = {
	/**
	 * Fetch input signals for a tenant with optional filters
	 * @param {Object} filters - Optional filters (event_category, event_status, salesforce_object_id)
	 * @param {string|Array<string>} filters.event_category - Single category or array of categories
	 * @returns {Promise<Array>} - Array of input signals
	 */
	getInputSignals: async (filters = {}, page = 1) => {
		const params = new URLSearchParams();
		params.append('page', page);

		if (filters.event_category) {
			// Handle both single string and array of categories
			if (Array.isArray(filters.event_category)) {
				// event_category=vdr,signal,news,report
				params.append('event_category', filters.event_category.join(','));
			} else {
				// event_category=alerts
				params.append('event_category', filters.event_category);
			}
		}

		if (
			filters.event_status === 'unacknowledged' ||
			filters.event_status === 'acknowledged'
		) {
			params.append('event_status', filters.event_status);
		}

		if (filters.salesforce_object_id) {
			params.append('salesforce_object_id', filters.salesforce_object_id);
		}

		const queryString = params.toString();
		const endpoint = `/input_signals${queryString ? `?${queryString}` : ''}`;

		const response = await authedApiFetch({
			endpoint,
			method: 'GET',
			returnResponse: true,
		});

		const data = await response.json();
		const totalCount = Number(response.headers.get('Total'));

		return {
			data,
			totalCount,
		};
	},

	// Fetch input signal stats for the selected alert status
	getInputSignalsStats: async (eventStatus) => {
		const params = new URLSearchParams();

		if (eventStatus === 'unacknowledged' || eventStatus === 'acknowledged') {
			params.append('event_status', eventStatus);
		}

		const queryString = params.toString();
		const endpoint = `/input_signals/stats${
			queryString ? `?${queryString}` : ''
		}`;

		const response = await authedApiFetch({
			endpoint,
			method: 'GET',
		});

		return response;
	},
	
	// Update an input signal's acknowledgement status
	updateAcknowledgementStatus: async (signalId, eventStatus) => {
		const validStatuses = ['acknowledged', 'unacknowledged'];

		if (!validStatuses.includes(eventStatus)) {
			throw new Error(`Invalid acknowledgement status: ${eventStatus}`);
		}

		const resp = authedApiFetch({
			endpoint: `/input_signals/${signalId}`,
			method: 'PATCH',
			payload: {
				input_signal: {
					event: {
						event_status: eventStatus,
					},
				},
			},
		});

		return resp;
	},
};

export default inputSignalsService;
